import { useEffect, useMemo, useState } from "react"
import { useQuery } from "urql"

import type { User, UserCreatedRecordType } from "@/graphql/codegen/graphql"
import { SortingOrder, UserSortField, UsersListDocument } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"
import { formatUserName } from "@/utils/user"

interface UseGetUserOptionsParams {
  createdRecordTypes?: UserCreatedRecordType[]
}

interface UseGetUserOptionsReturn {
  userOptions: Option<string>[]
  isFetching: boolean
}

const LIMIT = 100

export const useGetUserOptions = ({ createdRecordTypes }: UseGetUserOptionsParams = {}): UseGetUserOptionsReturn => {
  const [offset, setOffset] = useState(0)
  const [users, setUsers] = useState<User[]>([])

  const [{ data, fetching }] = useQuery({
    query: UsersListDocument,
    variables: {
      input: {
        offset,
        limit: LIMIT,
        filter: { createdRecordTypes },
        sort: { field: UserSortField.Name, order: SortingOrder.Asc },
      },
    },
  })

  const output = data?.user.list

  useEffect(() => {
    const paginatedUsers = output?.__typename === "UserListSuccess" ? (output.users as User[]) : []
    setUsers((prev) => (offset === 0 ? paginatedUsers : [...prev, ...paginatedUsers]))

    if (paginatedUsers.length === LIMIT) {
      setOffset((prevOffset) => prevOffset + LIMIT)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const userOptions = useMemo<Option<string>[]>(
    () => users.map((user) => ({ value: user.userId, label: formatUserName(user) })),
    [users]
  )

  return { userOptions, isFetching: fetching }
}
